import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Alert, FormControl, Typography } from "@mui/material";

interface QuickNewItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, description: string) => Promise<void>;
  title: string;
  category: string;
  error?: string | null;
  /** When true, description is required and the Create button stays disabled until provided. */
  requireDescription?: boolean;
}

export const QuickNewItemDialog: React.FC<QuickNewItemDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  category,
  error,
  requireDescription = false
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInternalClose = (_?: object, reason?: string) => {
    if (reason === "backdropClick") return;
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(name, description);
      setName("");
      setDescription("");
      onClose();
    } catch (error) {
      console.error(`Error creating new ${category}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleInternalClose}
      maxWidth='sm'
      fullWidth
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", pt: 1 }}>
          <FormControl sx={{ gap: "0.37rem" }}>
            <Typography htmlFor='name' variant='body1' component='label' sx={{ position: "unset", transform: "none", color: "grey.600" }}>
              {category} Name
            </Typography>
            <TextField
              autoFocus
              fullWidth
              required
              name={`name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Name'
              inputProps={{ maxLength: 100 }}
              helperText={`${name.length}/100`}
              sx={{
                "& > input": {
                  padding: "0.94rem 0.875rem",
                  height: "auto"
                },
                "& fieldset": {
                  borderColor: "grey.300",
                  borderRadius: "0.5rem"
                },
                "& .MuiFormHelperText-root": {
                  textAlign: "right",
                  color: name.length >= 100 ? "error.main" : "text.secondary"
                }
              }}
            />
          </FormControl>

          <FormControl sx={{ gap: "0.37rem" }}>
            <Typography htmlFor='name' variant='body1' component='label' sx={{ position: "unset", transform: "none", color: "grey.600" }}>
              Description{requireDescription && " *"}
            </Typography>
            <TextField
              name='description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              required={requireDescription}
              placeholder='Description'
              inputProps={{ maxLength: 250 }}
              helperText={`${description.length}/250`}
              sx={{
                "& > input": {
                  padding: "0.94rem 0.875rem",
                  height: "auto"
                },
                "& fieldset": {
                  borderColor: "grey.300",
                  borderRadius: "0.5rem"
                },
                "& .MuiFormHelperText-root": {
                  textAlign: "right",
                  color: description.length >= 250 ? "error.main" : "text.secondary"
                }
              }}
            />
          </FormControl>
        </Box>
        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='secondary-outlined'>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={!name.trim() || (requireDescription && !description.trim()) || isSubmitting}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
